/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Component,
  Input,
  signal,
  computed,
  ElementRef,
  HostListener,
  inject,
  forwardRef,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

@Component({
  selector: 'app-date-range-picker',
  imports: [CommonModule],
  templateUrl: './date-range-picker.html',
  styleUrl: './date-range-picker.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangePicker),
      multi: true
    }
  ]
})
export class DateRangePicker implements ControlValueAccessor, OnDestroy {
  private elementRef = inject(ElementRef);
  @Input() placeholder = 'Select duration';
  @Input() minDate: string | null = null;
  @Input() dropdownAlign: 'left' | 'right' = 'left';

  /** Sidebar + dual calendars + padding (see date-range-picker.scss). */
  private readonly dropdownWidth = 840;
  private readonly dropdownGap = 8;
  private readonly viewportPadding = 16;
  private readonly compactBreakpoint = 850;
  private bodyScrollLock = false;

  range = signal<DateRange>({ start: null, end: null });
  isOpen = signal(false);
  viewDate = signal(new Date());
  activePreset = signal<string | null>(null);
  compactView = signal(this.isCompactViewport());
  allowHorizontalScroll = signal(false);
  dropdownStyle = signal<Record<string, string>>({
    top: '0px',
    left: '0px',
    width: `${this.dropdownWidth}px`
  });
  weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  presets = [
    { id: 'today', label: 'Today' },
    { id: 'this_week', label: 'This Week' },
    { id: 'last_week', label: 'Last Week' },
    { id: 'last_15', label: 'Last 15 Days' },
    { id: 'month_to_date', label: 'Month Till Date' },
    { id: 'year_to_date', label: 'Year Till Date' },
    { id: 'custom', label: 'Custom Range' }
  ];

  formattedRange = computed(() => {
    const { start, end } = this.range();
    if (!start) return '';
    const f = (d: Date) =>
      `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getFullYear()}`;
    return end ? `${f(start)} - ${f(end)}` : f(start);
  });

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closePicker();
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.compactView.set(this.isCompactViewport());
    if (this.isOpen()) {
      this.updateDropdownPosition();
    }
  }

  @HostListener('document:scroll')
  onDocumentScroll() {
    if (this.isOpen() && !this.compactView()) {
      this.updateDropdownPosition();
    }
  }

  ngOnDestroy(): void {
    this.setBodyScrollLocked(false);
  }

  togglePicker() {
    if (this.isOpen()) {
      this.closePicker();
    } else {
      this.openPicker();
    }
  }

  openPicker() {
    this.isOpen.set(true);
    const start = this.range().start;
    this.viewDate.set(
      start
        ? new Date(start.getFullYear(), start.getMonth(), 1)
        : new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    );
    this.compactView.set(this.isCompactViewport());
    queueMicrotask(() => this.updateDropdownPosition());
    this.setBodyScrollLocked(this.compactView());
  }

  closePicker() {
    this.isOpen.set(false);
    this.allowHorizontalScroll.set(false);
    this.setBodyScrollLocked(false);
    this.onTouched();
  }

  prevMonth() {
    this.viewDate.set(new Date(this.viewDate().getFullYear(), this.viewDate().getMonth() - 1, 1));
    this.activePreset.set('custom');
  }

  nextMonth() {
    this.viewDate.set(new Date(this.viewDate().getFullYear(), this.viewDate().getMonth() + 1, 1));
    this.activePreset.set('custom');
  }

  nextMonthDate() {
    return new Date(this.viewDate().getFullYear(), this.viewDate().getMonth() + 1, 1);
  }

  formatMonth(date: Date) {
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  }

  calendarDays(baseDate: Date) {
    const year = baseDate.getFullYear();
    const month = baseDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const days: any[] = [];
    const prevLast = new Date(year, month, 0).getDate();

    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, prevLast - i), currentMonth: false });
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 1; i <= lastDay; i++) {
      const d = new Date(year, month, i);
      days.push({ date: d, currentMonth: true, isToday: d.getTime() === today.getTime() });
    }

    const rem = 42 - days.length;
    for (let i = 1; i <= rem; i++) {
      days.push({ date: new Date(year, month + 1, i), currentMonth: false });
    }
    return days;
  }

  onDateClick(date: Date) {
    this.activePreset.set('custom');
    const { start, end } = this.range();
    if (!start || (start && end)) {
      this.range.set({ start: new Date(date), end: null });
    } else {
      this.range.set(
        date < start ? { start: new Date(date), end: start } : { start, end: new Date(date) }
      );
    }
  }

  isDateSelected(date: Date) {
    const { start, end } = this.range();
    return (start && this.isSameDay(date, start)) || (end && this.isSameDay(date, end));
  }

  isDateInRange(date: Date) {
    const { start, end } = this.range();
    return start && end && date > start && date < end;
  }

  isRangeStart(date: Date) {
    const { start, end } = this.range();
    return start && this.isSameDay(date, start) && end !== null;
  }

  isRangeEnd(date: Date) {
    const { end } = this.range();
    return end && this.isSameDay(date, end);
  }

  isSameDay(d1: Date, d2: Date) {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }

  isDisabled(date: Date) {
    if (!this.minDate) return false;
    const m = new Date(this.minDate);
    m.setHours(0, 0, 0, 0);
    return date < m;
  }

  applyPreset(preset: any) {
    this.activePreset.set(preset.id);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let start = new Date(today);
    let end = new Date(today);

    switch (preset.id) {
      case 'today':
        break;
      case 'this_week': {
        const day = today.getDay();
        start.setDate(today.getDate() - day);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;
      }
      case 'last_week': {
        start.setDate(today.getDate() - today.getDay() - 7);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;
      }
      case 'last_15': {
        start.setDate(today.getDate() - 15);
        end = new Date();
        break;
      }
      case 'month_to_date': {
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date();
        break;
      }
      case 'year_to_date': {
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date();
        break;
      }
      case 'custom':
        return;
    }
    this.range.set({ start, end });
    this.viewDate.set(new Date(start.getFullYear(), start.getMonth(), 1));
  }

  clearValue(e?: Event) {
    if (e) {
      e.stopPropagation();
    }
    this.range.set({ start: null, end: null });
    this.onChange(null);
    this.activePreset.set(null);
  }

  apply() {
    const { start, end } = this.range();
    if (start) {
      this.onChange({ start: this.toISODate(start), end: end ? this.toISODate(end) : null });
      this.closePicker();
    }
  }

  private toISODate(d: Date) {
    return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
  }

  private isCompactViewport(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= this.compactBreakpoint;
  }

  private setBodyScrollLocked(locked: boolean): void {
    if (typeof document === 'undefined') {
      return;
    }

    if (locked) {
      if (!this.bodyScrollLock) {
        document.body.style.overflow = 'hidden';
        document.body.style.touchAction = 'none';
        this.bodyScrollLock = true;
      }
      return;
    }

    if (this.bodyScrollLock) {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      this.bodyScrollLock = false;
    }
  }

  private updateDropdownPosition(): void {
    if (this.compactView()) {
      this.allowHorizontalScroll.set(false);
      this.dropdownStyle.set({
        top: 'auto',
        left: '0',
        right: '0',
        width: '100%',
        bottom: '0',
        maxHeight: 'min(88dvh, calc(100vh - 1rem))'
      });
      return;
    }

    const input = this.elementRef.nativeElement.querySelector(
      '.input-wrapper'
    ) as HTMLElement | null;
    if (!input) {
      return;
    }

    const rect = input.getBoundingClientRect();
    const width = Math.min(this.dropdownWidth, window.innerWidth - this.viewportPadding * 2);
    let left = this.dropdownAlign === 'right' ? rect.right - width : rect.left;

    left = Math.max(
      this.viewportPadding,
      Math.min(left, window.innerWidth - width - this.viewportPadding)
    );

    const top = rect.bottom + this.dropdownGap;
    const maxHeight = window.innerHeight - top - this.viewportPadding;

    this.dropdownStyle.set({
      top: `${top}px`,
      left: `${left}px`,
      width: `${width}px`,
      bottom: 'auto',
      right: 'auto',
      maxHeight: `${Math.max(maxHeight, 320)}px`
    });

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => this.syncHorizontalScrollState());
    } else {
      queueMicrotask(() => this.syncHorizontalScrollState());
    }
  }

  private syncHorizontalScrollState(): void {
    if (this.compactView() || !this.isOpen()) {
      this.allowHorizontalScroll.set(false);
      return;
    }

    const main = this.elementRef.nativeElement.querySelector('.picker-main') as HTMLElement | null;
    if (!main) {
      this.allowHorizontalScroll.set(false);
      return;
    }

    this.allowHorizontalScroll.set(main.scrollWidth > main.clientWidth + 1);
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(v: any) {
    if (v && v.start) {
      this.range.set({ start: new Date(v.start), end: v.end ? new Date(v.end) : null });
    } else {
      this.range.set({ start: null, end: null });
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }
}
